import {
  createContext,
  ReactElement,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'

import { appConfig } from '@/services/config'
import { isInStandaloneMode } from '@/utils/isInStandaloneMode'
import { isMobileWeb } from '@/utils/utils'

export interface IBeforeInstallPromptEvent extends Event {
  readonly platforms: string[]
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed'
    platform: string
  }>
  prompt(): Promise<void>
}
export type PromptCtx = {
  pwaBeforeInstallPromptEvent: IBeforeInstallPromptEvent | null
  hidePrompt?: () => void
}
export type Children = {
  children: ReactElement | ReactElement[] | string | null
}

const PromptToInstall = createContext<PromptCtx>({
  pwaBeforeInstallPromptEvent: null,
})

export const PromptToInstallProvider = (props: Children) => {
  const [pwaBeforeInstallPromptEvent, setPwaBeforeInstallPromptEvent] =
    useState<IBeforeInstallPromptEvent | null>(null)

  const hidePrompt = useCallback(() => {
    setPwaBeforeInstallPromptEvent(null)
  }, [])

  useEffect(() => {
    if (
      !(appConfig.forceMobileWebPWA && isMobileWeb() && !isInStandaloneMode())
    ) {
      return
    }

    const ready = (e: IBeforeInstallPromptEvent) => {
      e.preventDefault()
      setPwaBeforeInstallPromptEvent(e)
    }

    window.addEventListener('load', async () => {
      window.addEventListener('beforeinstallprompt', ready)
    })

    return () => {
      window.removeEventListener('beforeinstallprompt', ready)
    }
  }, [])

  return (
    <PromptToInstall.Provider
      value={{ pwaBeforeInstallPromptEvent, hidePrompt }}
    >
      {props.children}
    </PromptToInstall.Provider>
  )
}

export function usePromptToInstall() {
  const ctx = useContext(PromptToInstall)
  if (!ctx) {
    throw new Error(
      'cannot use usePromptToInstall() outside <PromptToInstallProvider />'
    )
  }
  return ctx
}
